<template>
  <transition name="modal-fade">
    <div
      v-if="isVisible"
      class="spin-wheel-overlay"
      @click.self="handleOverlayClick"
    >
      <div class="spin-wheel-modal-content">
        <!-- Close Button -->
        <button class="close-btn" :disabled="isSpinning" @click="handleClose">
          <feather-icon icon="XIcon" size="20" />
        </button>

        <!-- Header -->
        <div class="modal-header-section">
          <h3 class="modal-title">🎡 กงล้อมหาโชค</h3>
          <p class="modal-subtitle">หมุนเพื่อรับรางวัล!</p>
        </div>

        <!-- Wheel Container -->
        <div class="wheel-container">
          <SpinWheel
            ref="spinWheel"
            :prizes="prizes"
            :size="wheelSize"
            :disabled="hasSpun || loading"
            :target-prize-index="targetPrizeIndex"
            :enable-sound="enableSound"
            :enable-haptic="enableHaptic"
            :enable-sparkles="enableSparkles"
            :sound-volume="soundVolume"
            :duration="4000"
            :verify="true"
            button-text="SPIN"
            @rotate-start="onRotateStart"
            @spin-start="onSpinStart"
            @spin-end="onSpinEnd"
          />
        </div>

        <!-- Success State Overlay -->
        <transition name="scale-fade">
          <div v-if="showSuccess" class="success-overlay">
            <div class="confetti-container">
              <div
                v-for="i in 50"
                :key="i"
                class="confetti"
                :style="getConfettiStyle(i)"
              />
            </div>
            <div class="reward-display">
              <div class="reward-icon">🎉</div>
              <h4 class="reward-title">ยินดีด้วย!</h4>
              <p class="reward-amount">
                คุณได้รับ {{ wonPrize ? wonPrize.value + ' เครดิต' : '' }}
              </p>
            </div>
          </div>
        </transition>

        <!-- Loading Overlay -->
        <transition name="fade">
          <div v-if="loading" class="loading-overlay">
            <b-spinner variant="warning" />
            <p>กำลังตรวจสอบ...</p>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SpinWheel from './wheel/SpinWheel.vue'

export default {
  name: 'SpinWheelModal',
  components: {
    BSpinner,
    SpinWheel
  },
  props: {
    id: {
      type: String,
      default: 'spin-wheel-modal'
    },
    value: {
      type: Boolean,
      default: false
    },
    // Audio/Haptic configuration
    enableSound: {
      type: Boolean,
      default: true
    },
    enableHaptic: {
      type: Boolean,
      default: true
    },
    enableSparkles: {
      type: Boolean,
      default: true
    },
    soundVolume: {
      type: Number,
      default: 0.5
    }
  },
  data() {
    return {
      isVisible: false,
      isSpinning: false,
      hasSpun: false,
      loading: false,
      showSuccess: false,
      wonPrize: null,
      targetPrizeIndex: null,
      wheelSize: 280,
      spinResult: null, // Stores backend response data

      prizes: [
        {
          id: 0,
          name: '500 Coin',
          value: 500,
          bgColor: '#ffcc00',
          color: '#1a1a2e',
          probability: 0
        },
        {
          id: 1,
          name: '10 Coin',
          value: 10,
          bgColor: '#ff9900',
          color: '#1a1a2e',
          probability: 55
        },
        {
          id: 2,
          name: '200 Coin',
          value: 200,
          bgColor: '#ff6600',
          color: '#ffffff',
          probability: 0
        },
        {
          id: 3,
          name: '20 Coin',
          value: 20,
          bgColor: '#e63946',
          color: '#ffffff',
          probability: 30
        },
        {
          id: 4,
          name: '50 Coin',
          value: 50,
          bgColor: '#9b59b6',
          color: '#ffffff',
          probability: 20
        },
        {
          id: 5,
          name: '100 Coin',
          value: 100,
          bgColor: '#2ecc71',
          color: '#ffffff',
          probability: 5
        }
      ]
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.isVisible = newVal
        if (!newVal) {
          this.resetState()
        } else {
          // Calculate wheel size based on viewport
          this.$nextTick(() => {
            this.calculateWheelSize()
          })
        }
      },
      immediate: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.calculateWheelSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateWheelSize)
  },
  methods: {
    calculateWheelSize() {
      // Mobile-first sizing: 80% of viewport width, max 320px
      const vw = Math.min(window.innerWidth * 0.8, 320)
      // Account for modal padding
      this.wheelSize = Math.max(240, vw - 40)
    },

    async onRotateStart(doSpin) {
      if (this.hasSpun || this.isSpinning || this.loading) return

      // Start loading - call API before spin
      this.loading = true

      try {
        const response = await this.performSpinApi()

        if (response.success) {
          // Map backend prize ID to frontend prize index
          const prizeIndex = this.mapPrizeIdToIndex(response.data.prize.id)
          this.targetPrizeIndex = prizeIndex
          this.spinResult = response.data // Store full result for display
          this.loading = false
          // Now trigger the spin with the target index
          this.$nextTick(() => {
            doSpin()
          })
        } else {
          this.loading = false
          if (this.$refs.spinWheel) {
            this.$refs.spinWheel.cancelVerify()
          }
          this.showErrorToast(
            response.message || 'ไม่สามารถหมุนได้ กรุณาลองใหม่'
          )
        }
      } catch (error) {
        this.loading = false
        if (this.$refs.spinWheel) {
          this.$refs.spinWheel.cancelVerify()
        }
        console.error('Spin error:', error)
        const errorMessage =
          error.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่'
        this.showErrorToast(errorMessage)
      }
    },

    onSpinStart() {
      this.isSpinning = true
    },

    onSpinEnd(result) {
      this.isSpinning = false
      this.hasSpun = true
      this.wonPrize = result.prize

      this.showSuccess = true
      this.showSuccessToast(result.prize)

      setTimeout(() => {
        this.$emit('input', false)
      }, 3000)
    },

    async performSpinApi() {
      // Call real API endpoint
      const response = await this.$http.post('/member/spin-wheel/spin')
      return response.data
    },

    mapPrizeIdToIndex(prizeId) {
      // Map backend prize ID (1-6) to frontend prize index (0-5)
      // Backend prizes: { id: 1, amount: 500 }, { id: 2, amount: 10 }, etc.
      // Frontend prizes array order: [500, 10, 200, 20, 50, 100]
      const prizeMap = {
        1: 0, // 500 coins
        2: 1, // 10 coins
        3: 2, // 200 coins
        4: 3, // 20 coins
        5: 4, // 50 coins
        6: 5 // 100 coins
      }
      return prizeMap[prizeId] ?? 0
    },

    showSuccessToast(prize) {
      // Use backend data if available
      const coinsWon = this.spinResult?.coins_won || prize.value
      const diamondsSpent = this.spinResult?.diamonds_spent || 100

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'รับรางวัลสำเร็จ!',
          text: `คุณได้รับ ${coinsWon} เหรียญ! (ใช้ ${diamondsSpent} เพชร)`,
          icon: 'GiftIcon',
          variant: 'success'
        }
      })

      // Emit event to parent to refresh balances
      if (this.spinResult) {
        this.$emit('spin-complete', {
          coinsWon: this.spinResult.coins_won,
          newCoinBalance: this.spinResult.new_coin_balance,
          newDiamondBalance: this.spinResult.new_diamond_balance,
          spinsRemaining: this.spinResult.spins_remaining
        })
      }
    },

    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'เกิดข้อผิดพลาด',
          text: message,
          icon: 'AlertTriangleIcon',
          variant: 'danger'
        }
      })
    },

    handleClose() {
      if (!this.isSpinning) {
        this.$emit('input', false)
      }
    },

    handleOverlayClick() {
      if (!this.isSpinning) {
        this.$emit('input', false)
      }
    },

    resetState() {
      setTimeout(() => {
        this.isSpinning = false
        this.hasSpun = false
        this.loading = false
        this.showSuccess = false
        this.wonPrize = null
        this.targetPrizeIndex = null
        this.spinResult = null
        // Reset wheel if component exists
        if (this.$refs.spinWheel) {
          this.$refs.spinWheel.reset()
        }
      }, 300)
    },

    getConfettiStyle(index) {
      const colors = [
        '#ffcc00',
        '#ff9900',
        '#ff6600',
        '#e63946',
        '#9b59b6',
        '#2ecc71',
        '#d4af37'
      ]
      const color = colors[index % colors.length]
      const left = Math.random() * 100
      const delay = Math.random() * 3
      const duration = 2 + Math.random() * 2
      const size = 6 + Math.random() * 6

      return {
        left: `${left}%`,
        backgroundColor: color,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        width: `${size}px`,
        height: `${size}px`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.spin-wheel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.spin-wheel-modal-content {
  position: relative;
  padding: 24px 16px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 20px;
  border: 2px solid rgba(212, 175, 55, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  min-height: 420px;
  width: 100%;
  max-width: 360px;

  @media (min-width: 768px) {
    padding: 32px 24px;
    min-height: 480px;
    max-width: 400px;
  }
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.modal-header-section {
  text-align: center;
  margin-bottom: 16px;
}

.modal-title {
  font-size: 22px;
  font-weight: bold;
  color: #d4af37;
  margin: 0 0 6px 0;

  @media (min-width: 768px) {
    font-size: 26px;
  }
}

.modal-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.wheel-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0;

  @media (min-width: 768px) {
    padding: 16px 0;
  }
}

.spin-info {
  text-align: center;
  margin-top: 12px;
}

.spin-count {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(212, 175, 55, 0.15);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 20px;
  font-size: 13px;
  color: #d4af37;

  &.spinning {
    animation: pulse 1s infinite;
  }
}

.success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 46, 0.95);
  z-index: 20;
  border-radius: 18px;
}

.confetti-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
}

.confetti {
  position: absolute;
  top: -10px;
  border-radius: 2px;
  animation: confetti-fall linear forwards;
}

.reward-display {
  text-align: center;
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  z-index: 1;
  padding: 20px;
}

.reward-icon {
  font-size: 80px;
  margin-bottom: 16px;
  animation: pulse 1s infinite;
  filter: drop-shadow(0 0 20px rgba(255, 204, 0, 0.5));

  @media (min-width: 768px) {
    font-size: 96px;
  }
}

.reward-title {
  font-size: 32px;
  font-weight: 800;
  color: #d4af37;
  margin: 0 0 16px 0;
  text-shadow: 0 0 20px rgba(212, 175, 55, 0.5);
  letter-spacing: 1px;

  @media (min-width: 768px) {
    font-size: 38px;
  }
}

.reward-amount {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  padding: 12px 24px;
  background: linear-gradient(135deg, #d4af37, #f5d675);
  color: #1a1a2e;
  border-radius: 30px;
  display: inline-block;
  box-shadow: 0 4px 20px rgba(212, 175, 55, 0.4);
  animation: glow 2s ease-in-out infinite;

  @media (min-width: 768px) {
    font-size: 32px;
    padding: 14px 32px;
  }
}

@keyframes glow {
  0%,
  100% {
    box-shadow: 0 4px 20px rgba(212, 175, 55, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(212, 175, 55, 0.7),
      0 0 40px rgba(212, 175, 55, 0.3);
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 46, 0.9);
  z-index: 15;
  border-radius: 18px;

  p {
    margin-top: 16px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
  }
}

// Animations
@keyframes confetti-fall {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(500px) rotate(720deg);
    opacity: 0;
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

// Transitions
.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.scale-fade-enter,
.scale-fade-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Modal transition
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .spin-wheel-modal-content,
.modal-fade-leave-active .spin-wheel-modal-content {
  transition: transform 0.3s ease;
}

.modal-fade-enter .spin-wheel-modal-content,
.modal-fade-leave-to .spin-wheel-modal-content {
  transform: scale(0.9);
}
</style>
